import { useState } from "react";
import { API } from "../../../../services/api";
import { ADMIN_API_CONFIG } from "../controleConstants";
import { PATIENT_EDITABLE_FIELDS } from "../controleVariants";

const NEVER_FROZEN = () => false;

function patientPayload(source, patch) {
    const payload = Object.fromEntries(PATIENT_EDITABLE_FIELDS.map(field => [field, source[field] ?? null]));
    return Object.assign(payload, patch);
}

// Modifications de la fiche administrative d'un patient depuis un dossier de contrôle.
// Le PUT remplace toute la fiche : chaque champ de PATIENT_EDITABLE_FIELDS est relu sur la fiche à jour.
export function usePatientEditing(patients, { isFrozen = NEVER_FROZEN, onError }) {
    const { loadPatientDetail, replacePatient, setPatients, setSelectedPatientId, pdaRemarkDraft, setPdaRemarkDraft } = patients;
    const [identityRow, setIdentityRow] = useState(null);
    const [pdaRemarkSaving, setPdaRemarkSaving] = useState(false);

    const openIdentity = async patient => {
        if (isFrozen(patient?.patientId)) return;
        const detail = await loadPatientDetail(patient.patientId);
        if (detail) setIdentityRow(detail);
    };

    const saveIdentity = async (row, identity) => {
        if (isFrozen(row?.patientId)) return;
        const latest = await loadPatientDetail(row.patientId);
        const response = await API.put(`/administration/patients/${row.patientId}`, patientPayload(latest || row, identity), ADMIN_API_CONFIG);
        replacePatient(response.data);
        setIdentityRow(current => (
            current && String(current.patientId) === String(response.data.patientId) ? response.data : current
        ));
    };

    const savePatch = async (patient, patch, fallbackError) => {
        if (!patient?.patientId || isFrozen(patient.patientId)) return null;
        onError("");
        const latest = await loadPatientDetail(patient.patientId);
        return API.put(`/administration/patients/${patient.patientId}`, patientPayload(latest || patient, patch), ADMIN_API_CONFIG)
            .then(response => {
                replacePatient(response.data);
                setPdaRemarkDraft(response.data.pdaProductionRemark || "");
                return response.data;
            })
            .catch(requestError => {
                onError(requestError.response?.data?.message || fallbackError);
                return null;
            });
    };

    const savePdaRemark = async patient => {
        if (isFrozen(patient?.patientId)) return;
        setPdaRemarkSaving(true);
        try {
            await savePatch(patient, { pdaProductionRemark: pdaRemarkDraft.trim() || null }, "Impossible d'enregistrer la remarque PDA.");
        } finally {
            setPdaRemarkSaving(false);
        }
    };

    const deletePatient = async patientId => {
        if (isFrozen(patientId)) return;
        await API.delete(`/administration/patients/${patientId}`, ADMIN_API_CONFIG);
        setPatients(current => current.filter(row => String(row.patientId) !== String(patientId)));
        setSelectedPatientId(current => (String(current) === String(patientId) ? "" : current));
        setIdentityRow(null);
    };

    return { identityRow, setIdentityRow, openIdentity, saveIdentity, savePatch, pdaRemarkSaving, savePdaRemark, deletePatient };
}
